import React, { useState } from "react";

import ConfirmProxyBidModal from "./ConfirmProxyBidModal";
import YourProxyBid from "./YourProxyBid";
import ProxyBidCard from "./ProxyBidCard";
import trackVehicle from "../../api/trackVehicle";
import DeleteProxyBidConfirmationModal from "./DeleteProxyBidConfirmationModal";
import deleteProxyBid from "../../api/deleteProxyBid";
import pushToDigitalData from "../../analytics/pushToDigitalData";

interface ProxyBidProps {
  increment: number;
  bidAmount: number;
  existingBid: boolean;
  termsAndConditionsNeedAccepting: boolean;
  termsAndConditionsLink: string;
  isTracked: boolean;
  vrm: string | string[];
  acceptTermsAndConditions: () => Promise<void>;
  onPlaceBid: () => Promise<void>;
  setDeleteBidStatus: (value: string) => void;
  setBidAmount: (bidAmount: number) => void;
  setIsTracked: (value: boolean) => void;
  inventoryId: string | null;
  isToUseAuthZero?: boolean;
  accessToken?: string;
}

const ProxyBid: React.FunctionComponent<ProxyBidProps> = ({
  increment,
  bidAmount,
  existingBid,
  termsAndConditionsNeedAccepting,
  termsAndConditionsLink,
  isTracked,
  vrm,
  acceptTermsAndConditions,
  onPlaceBid,
  setDeleteBidStatus,
  setBidAmount,
  setIsTracked,
  inventoryId,
  isToUseAuthZero = false,
  accessToken,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);
  const handleConfirmProxyBid = () => {
    setIsLoading(true);
    if (termsAndConditionsNeedAccepting) {
      return acceptTermsAndConditions().then(() => {
        onPlaceBid().then(async () => {
          if (!isTracked) {
            const result = await trackVehicle(vrm, inventoryId, isToUseAuthZero, accessToken);

            if (result.status > 200 || result?.degradedServices?.length > 0) {
              setIsTracked(false);
            } else {
              setIsTracked(true);
            }
          }
          setIsLoading(false);
          setIsEdit(false);
          setShowModal(false);
        });
      });
    }

    onPlaceBid().then(async () => {
      if (!isTracked) {
        const result = await trackVehicle(vrm, inventoryId, isToUseAuthZero, accessToken);

        if (result.status > 200 || result?.degradedServices?.length > 0) {
          setIsTracked(false);
        } else {
          setIsTracked(true);
        }
      }
      setIsLoading(false);
      setIsEdit(false);
      setShowModal(false);
    });
  };

  const onEdit = () => {
    setIsEdit(true);
  };

  const onDeleteProxyBid = () => {
    setShowDeleteConfirmationModal(true);
  };

  const onConfirmDeleteProxyBid = async () => {
    setIsLoading(true);
    await deleteProxyBid(vrm, isToUseAuthZero, accessToken)
      .then(() => {
        setDeleteBidStatus("success");
        setIsLoading(false);
        setShowDeleteConfirmationModal(false);
        pushToDigitalData({
          event: {
            name: "click",
            category: "button",
            label: "proxyBid",
            action: "delete-confirmation",
            section: "",
          },
        });
      })
      .catch(() => {
        setDeleteBidStatus("error");
      });
    setDeleteBidStatus("idle");
  };

  const title = isEdit ? "Edit your Proxy Bid" : "Proxy Bid";
  const submitButtonText = isEdit ? "Update Proxy Bid" : "Place Proxy Bid";

  return (
    <>
      {((existingBid && isEdit) || !existingBid) && (
        <ProxyBidCard
          title={title}
          text="Set a maximum bid now and our system will automatically bid on your behalf during the live auction. Up to your chosen amount."
          increment={increment}
          buttonText={submitButtonText}
          bidAmount={bidAmount}
          setBidAmount={setBidAmount}
          onSubmit={onSubmit}
        ></ProxyBidCard>
      )}
      {!isEdit && existingBid && (
        <YourProxyBid
          bidAmount={bidAmount ? bidAmount / 100 : 0}
          onEdit={onEdit}
          onDeleteProxyBid={onDeleteProxyBid}
        ></YourProxyBid>
      )}

      {showModal && (
        <ConfirmProxyBidModal
          onClose={handleCloseModal}
          onConfirm={handleConfirmProxyBid}
          bidAmount={bidAmount ? bidAmount / 100 : 0}
          isOpen={showModal}
          termsAndConditionsNeedAccepting={termsAndConditionsNeedAccepting}
          termsAndConditionsLink={termsAndConditionsLink}
          isLoading={isLoading}
        ></ConfirmProxyBidModal>
      )}

      {showDeleteConfirmationModal && (
        <DeleteProxyBidConfirmationModal
          isOpen={showDeleteConfirmationModal}
          isProxyBidsDataLoading={isLoading}
          onClose={() => {
            setShowDeleteConfirmationModal(false);
          }}
          onConfirm={onConfirmDeleteProxyBid}
        />
      )}
    </>
  );
};

export default ProxyBid;
